import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import { listUsers, updateUserRole, updateUserPosition, updateUserOrgLevel, type UserDto } from '../../lib/users';
import { listDivisions } from '../../lib/divisions';
import { listPositions, type PositionDto } from '../../lib/positions';
import { listOrgLevels, type OrgLevelDto } from '../../lib/orgLevels';
import { ROLE_LABELS, ROLE_COLORS, type UserRole } from '../../lib/roles';
import { listScoresForUser, type CompetencyScoreDto } from '../../lib/competencyScores';
import { listCompetencies, type CompetencyDto } from '../../lib/competencies';
import { listAssessmentTemplates, type AssessmentTemplateDto } from '../../lib/assessmentTemplates';
import { assignSession } from '../../lib/assessmentSessions';
import { ArrowLeft, BarChart2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/org/employees/$employeeId')({
  component: EmployeeProfilePage,
});

function EmployeeProfilePage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <EmployeeProfileContent />
    </ProtectedRoute>
  );
}

type Tab = 'overview' | 'competency-scores';

function EmployeeProfileContent() {
  const { employeeId } = Route.useParams();
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:users');

  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const [employee, setEmployee] = useState<UserDto | null>(null);
  const [divisionName, setDivisionName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingRole, setEditingRole] = useState(false);
  const [newRole, setNewRole] = useState<UserRole>('employee');
  const [roleLoading, setRoleLoading] = useState(false);

  // Position
  const [positions, setPositions] = useState<PositionDto[]>([]);
  const [selectedPositionId, setSelectedPositionId] = useState<string>('__none__');
  const [positionLoading, setPositionLoading] = useState(false);

  // Org Level
  const [orgLevels, setOrgLevels] = useState<OrgLevelDto[]>([]);
  const [selectedOrgLevelId, setSelectedOrgLevelId] = useState<string>('__none__');
  const [orgLevelLoading, setOrgLevelLoading] = useState(false);

  // Competency scores tab
  const [scores, setScores] = useState<CompetencyScoreDto[]>([]);
  const [competencies, setCompetencies] = useState<CompetencyDto[]>([]);
  const [scoresLoading, setScoresLoading] = useState(false);
  const [scoresLoaded, setScoresLoaded] = useState(false);

  // Assign assessment modal
  const [showAssign, setShowAssign] = useState(false);
  const [assignTemplates, setAssignTemplates] = useState<AssessmentTemplateDto[]>([]);
  const [assignTemplatesLoaded, setAssignTemplatesLoaded] = useState(false);
  const [assignTemplateId, setAssignTemplateId] = useState('');
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [usersRes, divsRes, posRes, lvlRes] = await Promise.all([
        listUsers(companyId, {}),
        listDivisions(companyId),
        listPositions(companyId, false),
        listOrgLevels(companyId),
      ]);
      if (cancelled) return;
      setLoading(false);
      if (posRes.success && posRes.data) setPositions(posRes.data.positions);
      if (lvlRes.success && lvlRes.data) setOrgLevels(lvlRes.data.orgLevels);
      if (usersRes.success && usersRes.data) {
        const found = usersRes.data.users.find((u) => u.id === employeeId);
        if (found) {
          setEmployee(found);
          setNewRole(found.role);
          setSelectedPositionId(found.positionId ?? '__none__');
          setSelectedOrgLevelId(found.orgLevelId ?? '__none__');
          if (found.divisionId && divsRes.success && divsRes.data) {
            const div = divsRes.data.divisions.find((d) => d.id === found.divisionId);
            setDivisionName(div?.name ?? found.divisionId);
          }
        } else {
          setError('Employee not found');
        }
      } else {
        setError('Failed to load employee data');
      }
    }
    load();
    return () => { cancelled = true; };
  }, [employeeId, companyId]);

  // Lazy-load assessment templates when assign modal opens
  useEffect(() => {
    if (!showAssign || assignTemplatesLoaded) return;
    let cancelled = false;
    listAssessmentTemplates(companyId, true).then((res) => {
      if (cancelled) return;
      setAssignTemplatesLoaded(true);
      if (res.success && res.data) setAssignTemplates(res.data.templates);
    });
    return () => { cancelled = true; };
  }, [showAssign, assignTemplatesLoaded, companyId]);

  async function handleAssignSession(e: React.FormEvent) {
    e.preventDefault();
    if (!assignTemplateId) return;
    setAssigning(true);
    setAssignError('');
    setAssignSuccess('');
    const res = await assignSession(
      { companyId, templateId: assignTemplateId, assignedToUserId: employeeId, assignedByUserId: user?.id, dueDate: assignDueDate || undefined },
    );
    setAssigning(false);
    if (res.success) {
      const tpl = assignTemplates.find((t) => t.id === assignTemplateId);
      toast.success(`Assessment "${tpl?.name ?? 'Assessment'}" assigned to ${employee?.name ?? 'employee'}`);
      setAssignSuccess(`Session "${tpl?.name ?? 'Assessment'}" assigned successfully.`);
      setAssignTemplateId('');
      setAssignDueDate('');
    } else {
      toast.error(res.error ?? 'Failed to assign assessment');
      setAssignError(res.error ?? 'Failed to assign');
    }
  }

  // Lazy-load competency scores when tab becomes active
  useEffect(() => {
    if (activeTab !== 'competency-scores' || scoresLoaded) return;
    let cancelled = false;
    setScoresLoading(true);
    Promise.all([
      listScoresForUser(employeeId, companyId, true),
      listCompetencies({ companyId }),
    ]).then(([scoresRes, compRes]) => {
      if (cancelled) return;
      setScoresLoading(false);
      setScoresLoaded(true);
      if (scoresRes.success && scoresRes.data) setScores(scoresRes.data.scores);
      if (compRes.success && compRes.data) setCompetencies(compRes.data.competencies);
    });
    return () => { cancelled = true; };
  }, [activeTab, scoresLoaded, employeeId, companyId]);

  async function handleRoleChange() {
    if (!employee) return;
    setRoleLoading(true);
    const res = await updateUserRole(employee.id, companyId, newRole);
    setRoleLoading(false);
    if (res.success && res.data) {
      toast.success('Role updated');
      setEmployee(res.data.user);
      setEditingRole(false);
    } else {
      toast.error(res.error ?? 'Failed to update role');
    }
  }

  async function handlePositionChange(posId: string) {
    if (!employee) return;
    setPositionLoading(true);
    const resolved = posId === '__none__' ? null : posId;
    const res = await updateUserPosition(employee.id, companyId, resolved);
    setPositionLoading(false);
    if (res.success && res.data) {
      toast.success('Position updated');
      setEmployee(res.data.user);
      setSelectedPositionId(posId);
    } else {
      toast.error(res.error ?? 'Failed to update position');
    }
  }

  async function handleOrgLevelChange(lvlId: string) {
    if (!employee) return;
    setOrgLevelLoading(true);
    const resolved = lvlId === '__none__' ? null : lvlId;
    const res = await updateUserOrgLevel(employee.id, companyId, resolved);
    setOrgLevelLoading(false);
    if (res.success && res.data) {
      toast.success('Org level updated');
      setEmployee(res.data.user);
      setSelectedOrgLevelId(lvlId);
    } else {
      toast.error(res.error ?? 'Failed to update org level');
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-40 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground text-sm">{error || 'Employee not found'}</p>
        <Link to="/org/" className="text-sm text-primary underline mt-2 block">Back to Org</Link>
      </div>
    );
  }

  const TABS: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'competency-scores', label: 'Competency Scores' },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Back + Assign button */}
      <div className="flex items-center justify-between">
        <Link to="/org/">
          <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2">
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Organization
          </Button>
        </Link>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => { setShowAssign(true); setAssignSuccess(''); setAssignError(''); }}>
            <UserPlus className="w-3.5 h-3.5 mr-1.5" />
            Assign Assessment
          </Button>
        )}
      </div>

      {/* Profile card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center text-2xl font-bold text-brand-500 shrink-0">
              {employee.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground">{employee.name}</h1>
              <p className="text-sm text-muted-foreground">{employee.email}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[employee.role]}`}>
                  {ROLE_LABELS[employee.role]}
                </span>
                {!employee.isActive && (
                  <Badge variant="destructive">Inactive</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Details grid */}
          <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium tracking-wide">Division</p>
              <p className="text-foreground mt-0.5">{divisionName || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium tracking-wide">Position</p>
              <p className="text-foreground mt-0.5">
                {employee.positionId
                  ? (positions.find((p) => p.id === employee.positionId)?.title ?? '—')
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium tracking-wide">Org Level</p>
              <p className="text-foreground mt-0.5">
                {employee.orgLevelId
                  ? (() => { const l = orgLevels.find((l) => l.id === employee.orgLevelId); return l ? `L${l.level} – ${l.name}` : '—'; })()
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium tracking-wide">Joined</p>
              <p className="text-foreground mt-0.5">{new Date(employee.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium tracking-wide">Last Login</p>
              <p className="text-foreground mt-0.5">
                {employee.lastLoginAt ? new Date(employee.lastLoginAt).toLocaleDateString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-medium tracking-wide">Status</p>
              <p className={cn('mt-0.5 font-medium text-sm', employee.isActive ? 'text-emerald-600' : 'text-destructive')}>
                {employee.isActive ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && canManage && (
        <div className="space-y-4">
          {/* Role management */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Role Management</CardTitle>
                {!editingRole && (
                  <button
                    type="button"
                    onClick={() => setEditingRole(true)}
                    className="text-xs text-primary hover:underline"
                  >
                    Change Role
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingRole ? (
                <div className="flex items-center gap-3">
                  <select
                    className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as UserRole)}
                  >
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                    <option value="hr">HR</option>
                    <option value="admin">Company Admin</option>
                  </select>
                  <Button size="sm" onClick={handleRoleChange} disabled={roleLoading}>
                    {roleLoading ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingRole(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Current role: <span className="font-medium text-foreground">{ROLE_LABELS[employee.role]}</span>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Position assignment */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Position Assignment</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                The employee's position determines the competency levels required in their IDP.
              </p>
              <div className="flex items-center gap-3">
                <select
                  className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  value={selectedPositionId}
                  onChange={(e) => handlePositionChange(e.target.value)}
                  disabled={positionLoading}
                >
                  <option value="__none__">— No position assigned —</option>
                  {positions.map((p) => (
                    <option key={p.id} value={p.id}>
                      [{p.code}] {p.title}
                    </option>
                  ))}
                </select>
                {positionLoading && <span className="text-xs text-muted-foreground">Saving…</span>}
              </div>
            </CardContent>
          </Card>

          {/* Org Level assignment */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Org Level</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                Seniority level within the company hierarchy (e.g. Member, Manager, Director).
              </p>
              <div className="flex items-center gap-3">
                <select
                  className="flex-1 border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  value={selectedOrgLevelId}
                  onChange={(e) => handleOrgLevelChange(e.target.value)}
                  disabled={orgLevelLoading}
                >
                  <option value="__none__">— No level assigned —</option>
                  {orgLevels.sort((a, b) => a.level - b.level).map((l) => (
                    <option key={l.id} value={l.id}>L{l.level} – {l.name}</option>
                  ))}
                </select>
                {orgLevelLoading && <span className="text-xs text-muted-foreground">Saving…</span>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Assign Assessment dialog */}
      <Dialog open={showAssign} onOpenChange={(open) => { if (!open) setShowAssign(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Assessment to {employee.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAssignSession} className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label>Template</Label>
              <Select value={assignTemplateId} onValueChange={setAssignTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder={assignTemplatesLoaded ? 'Select template…' : 'Loading…'} />
                </SelectTrigger>
                <SelectContent>
                  {assignTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="emp-due-date">Due date (optional)</Label>
              <Input id="emp-due-date" type="date" value={assignDueDate} onChange={(e) => setAssignDueDate(e.target.value)} />
            </div>
            {assignError && <p className="text-xs text-destructive">{assignError}</p>}
            {assignSuccess && <p className="text-xs text-emerald-600">{assignSuccess}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssign(false)}>Close</Button>
              <Button type="submit" disabled={assigning || !assignTemplateId}>
                {assigning ? 'Assigning…' : 'Assign Session'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Competency Scores tab */}
      {activeTab === 'competency-scores' && (
        <div className="space-y-3">
          {scoresLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : scores.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center py-12 text-center">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center mb-3">
                  <BarChart2 className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">No competency scores yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Scores will appear after the employee completes an assessment session.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Showing the latest score per competency.
              </p>
              {scores.map((score) => {
                const comp = competencies.find((c) => c.id === score.competencyId);
                const pct = Math.min(100, Math.round((score.score / 5) * 100));
                return (
                  <Card key={score.id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {comp?.name ?? score.competencyId}
                          </p>
                          {comp?.code && (
                            <p className="text-xs text-muted-foreground">{comp.code}</p>
                          )}
                        </div>
                        <div className="shrink-0 ml-4 text-right">
                          <p className="text-lg font-bold text-primary">{score.score.toFixed(1)}</p>
                          <p className="text-xs text-muted-foreground">/ 5.0</p>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
                        <span>Source: {score.source}</span>
                        <span>{new Date(score.assessedAt).toLocaleDateString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
